import { Autocomplete, AutocompletePaper } from '@components/autocomplete';
import { Item } from '@components/index';
import { TextField } from '@components/textField/TextField';
import { useStore } from '@store/GlobalStore';
import { DEBOUNCE_DELAYS } from '@constants/debounceDelays';
import { BrokerageUserRole } from '@lio/rule-engine';
import { Typography, AutocompleteRenderInputParams, ThemeProvider, Grid, Theme } from '@mui/material';
import { boxStyles } from '@pages/NewQuote/scope/ScopePanel.styles';
import { AddEditUserModal } from '@pages/PortalAdmin/UserManagement/AddEditUserModal';
import { UserModalType, UserModalTypes } from '@pages/PortalAdmin/UserManagement/UserManagement.constants';
import { useBrokerages, useSearchPaginatedBrokerages, useSearchPaginatedBrokerageUsers } from '@services/brokerage';
import { useNewQuoteStore } from '@store/NewQuote';
import { handleScrollToBottom } from '@utils/HandleScrollToBottom';
import { useState, useMemo, HTMLAttributes, useCallback, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { shallow } from 'zustand/shallow';
import {
  BrokerageProducerSearchResponse,
  UserPostResponse,
  BrokerageSearchResponse,
  BrokerageUserSearchResponse,
} from '@lio/api-types';
import { useQueryClient } from '@tanstack/react-query';
import composeTheme from '@theme/theme';
import { useSearchPaginatedBrokerageProducers } from '@services/brokerage/user/searchPaginatedBrokerageProducers';
import { BrokerListItem } from '@pages/NewQuote/scope/inputSelection/BrokerListItem';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { brokerageProducerKeys } from '@services/brokerage/user/user.keys';
import { ArrayItem } from '@utils/commonTypes';
type BrokerageProducerChangeData = {
  brokerage?: { id: string; name: string };
  brokerageProducers?: Array<{ id: string }>;
  user: { firstName: string; lastName: string };
};

type TBrokerageSelection = {
  handleAutocompleteClear: (step: number) => void;
  handleBrokerageChange: (selected: ArrayItem<BrokerageSearchResponse['items']> | undefined) => void;
  handleBrokerageProducerChange: (selected: BrokerageProducerChangeData | null) => void;
};

type AddProducerOption = {
  isAddNewProducerVisible: boolean;
  handleAddNewProducer: () => void;
};

function AddNewProducerOption({ isAddNewProducerVisible, handleAddNewProducer, ...rest }: AddProducerOption) {
  return (
    <AutocompletePaper
      textButton={isAddNewProducerVisible ? 'Add New Producer' : undefined}
      textButtonOnClick={isAddNewProducerVisible ? handleAddNewProducer : undefined}
      {...rest}
    />
  );
}

export const AgencySelection = ({
  handleBrokerageChange,
  handleBrokerageProducerChange,
  handleAutocompleteClear,
}: TBrokerageSelection) => {
  const { isInternalUser, isAdmin, isUnderwriter, isLIOContractor, isPortalAdmin, isJrOps, isSrOps } = useStore(
    (state) => state.userAuth,
    shallow,
  );
  const isAddNewProducerVisible = isAdmin || isUnderwriter || isLIOContractor || isPortalAdmin || isJrOps || isSrOps;
  const { scope, updateScopeFields } = useNewQuoteStore();
  const {
    brokerageAgency,
    brokerageProducer,
    productOffering,
    insuranceProductId,
    productOption,
    productLabel,
    effectiveDate,
    expirationDate,
    writingCompany,
    isSpecialEvent,
  } = scope;
  const [modalType, setModalType] = useState<UserModalType | undefined>();
  const [newUserId, setNewUserId] = useState<string | undefined>();
  const [brokerageSearchbarText, setBrokerageSearchbarText] = useState<string>('');
  const [brokerageUserSearchbarText, setBrokerageUserSearchbarText] = useState<string>('');
  const { brokerages } = useBrokerages({ enabled: isInternalUser, sort: '+name' });
  const { isMobileScreen } = useGetScreenSize();
  const queryClient = useQueryClient();

  const debouncedBrokerageSearchbarText = useDebouncedCallback((value) => {
    setBrokerageSearchbarText(value);
  }, DEBOUNCE_DELAYS.TYPING);

  const debouncedBrokerageUserSearchbarText = useDebouncedCallback((value) => {
    setBrokerageUserSearchbarText(value);
  }, DEBOUNCE_DELAYS.TYPING);

  const {
    searchBrokerage,
    isLoading: isSearchBrokeragesLoading,
    fetchNextPage: loadMoreBrokerageResults,
  } = useSearchPaginatedBrokerages(isInternalUser, {
    sort: '+name',
    pageLimit: 50,
    name: brokerageSearchbarText,
  });
  const {
    brokerageUsers: searchBrokerageProducersByBrokerage,
    isLoading: isSearchBrokerageProducersByBrokerageByBrokerageLoading,
    fetchNextPage: loadMoreBrokerageProducerByBrokerageResults,
  } = useSearchPaginatedBrokerageUsers(brokerageAgency?.value ? { brokerageId: brokerageAgency.value } : undefined, {
    role: BrokerageUserRole.Producer,
    sort: '+user.firstName, +user.lastName',
    pageLimit: 25,
    searchText: brokerageUserSearchbarText,
  });

  const {
    brokerageProducers: searchBrokerageProducers,
    isLoading: isSearchBrokerageProducersLoading,
    fetchNextPage: loadMoreBrokerageProducerResults,
  } = useSearchPaginatedBrokerageProducers({
    sort: '+user.firstName, +user.lastName',
    pageLimit: 25,
    searchText: brokerageUserSearchbarText,
  });

  const allBrokerages = searchBrokerage?.pages?.flatMap<ArrayItem<BrokerageSearchResponse['items']>>(
    (page) => page.items,
  );
  const allBrokerageUserProducers = useMemo(
    () =>
      searchBrokerageProducersByBrokerage?.pages?.flatMap<ArrayItem<BrokerageUserSearchResponse['items']>>(
        (page) => page.items,
      ),
    [searchBrokerageProducersByBrokerage?.pages],
  );

  const allProducers = useMemo(
    () =>
      searchBrokerageProducers?.pages?.flatMap<ArrayItem<BrokerageProducerSearchResponse['items']>>(
        (page) => page.items,
      ),
    [searchBrokerageProducers?.pages],
  );

  useEffect(() => {
    if (newUserId && allBrokerageUserProducers && allBrokerageUserProducers.length > 0) {
      const newProducerData = allBrokerageUserProducers.find((item) => item?.user.id === newUserId);
      if (newProducerData && newProducerData.brokerageProducers?.[0]?.id) {
        // Get current scope values at the time of execution
        const currentScope = {
          brokerageAgency,
          brokerageProducer,
          productOffering,
          insuranceProductId,
          productOption,
          productLabel,
          effectiveDate,
          expirationDate,
          writingCompany,
          isSpecialEvent,
        };

        updateScopeFields({
          ...currentScope,
          brokerageProducer: {
            value: newProducerData.brokerageProducers[0].id,
            label: `${newProducerData.user.firstName} ${newProducerData.user.lastName}`,
          },
        });
        setNewUserId(undefined);
      }
    }
    // eslint-disable-next-line
  }, [newUserId, allBrokerageUserProducers]);

  const handleAddNewProducer = useCallback(() => {
    setModalType(UserModalTypes.CreateUser);
  }, []);

  const handleCloseModal = () => {
    setModalType(undefined);
  };

  const handleNewBrokerageUser = (newUser: UserPostResponse) => {
    queryClient.invalidateQueries({ queryKey: ['brokerageUser', { brokerageId: brokerageAgency?.value }] });
    queryClient.invalidateQueries({
      queryKey: brokerageProducerKeys.searchPaginated({ brokerageId: brokerageAgency?.value }, ''),
    });
    setBrokerageUserSearchbarText(newUser.email);
    setNewUserId(newUser.id);
  };

  const getAddNewProducerOptionComponent = useMemo(
    () => (props: HTMLAttributes<HTMLElement>) => {
      return AddNewProducerOption({ isAddNewProducerVisible, handleAddNewProducer, ...props });
    },
    [isAddNewProducerVisible, handleAddNewProducer],
  );

  return (
    <>
      <Typography variant='h4'>Who is the agency and assigned broker?</Typography>
      <Grid sx={boxStyles} size={{ xs: 12 }}>
        <Autocomplete
          filterOptions={(x) => x}
          openOnFocus
          sx={!isMobileScreen ? { width: '300px' } : undefined}
          data-testid='Brokerage'
          fullWidth
          loading={isSearchBrokeragesLoading}
          // isOptionEqualToValue={(option: Item, value: Item) => option.value === value?.value}
          options={
            allBrokerages
              ? allBrokerages.map((item) => ({
                  value: item.id,
                  label: item.name,
                }))
              : []
          }
          onInputChange={(event, newInputValue) => {
            if (event) {
              debouncedBrokerageSearchbarText(newInputValue);
            }
          }}
          value={brokerageAgency ?? null}
          onChange={(_event, value, reason) => {
            if (reason === 'clear') {
              handleAutocompleteClear(0);
              setBrokerageSearchbarText('');
              setBrokerageUserSearchbarText('');
            } else {
              const selectedValue = allBrokerages?.find((b) => b.id === (value as Item | undefined)?.value);
              handleBrokerageChange(selectedValue);
            }
          }}
          disabled={!brokerages?.length}
          renderOption={(props, option) =>
            (option as Item)?.value === '' || (option as Item)?.hidden ? null : (
              <li {...props} key={(option as Item)?.value} data-testid={option.value}>
                <Typography
                  variant='bodySmall'
                  sx={{
                    color: (theme: Theme) => (theme.palette.theme === 'lio' ? 'common.black' : 'text.main'),
                  }}
                >
                  {(option as Item).label}
                </Typography>
              </li>
            )
          }
          renderInput={(params: AutocompleteRenderInputParams) => {
            return (
              <TextField
                slotProps={{
                  htmlInput: {
                    ...params.inputProps,
                  },
                }}
                inputLabel='Brokerage'
                {...params}
              />
            );
          }}
          slotProps={{
            listbox: {
              onScroll: (e) => handleScrollToBottom(e, loadMoreBrokerageResults),
              style: {
                maxHeight: '300px',
              },
            },
          }}
        />
      </Grid>

      <Grid sx={boxStyles} size={{ xs: 12 }}>
        {isInternalUser && !brokerageAgency ? (
          <Autocomplete
            disablePortal
            filterOptions={(x) => x}
            sx={!isMobileScreen ? { width: '300px' } : undefined}
            data-testid='Producer'
            fullWidth
            loading={isSearchBrokerageProducersLoading}
            isOptionEqualToValue={(option: Item, value: Item) => option.value === value?.value}
            options={[
              ...(allProducers
                ? allProducers.map((item) => ({
                    key: item.id,
                    value: item.id,
                    label: `${item.user.firstName} ${item.user.lastName}`,
                    email: item.user.email,
                  }))
                : []),
            ]}
            onInputChange={(event, newInputValue) => {
              if (event) {
                debouncedBrokerageUserSearchbarText(newInputValue);
              }
            }}
            value={brokerageProducer ?? null}
            onChange={(_event, value, reason) => {
              if (reason === 'clear') {
                handleAutocompleteClear(1);
                setBrokerageUserSearchbarText('');
                setBrokerageSearchbarText('');
              } else {
                const selectedValue = allProducers?.find((b) => b?.id === (value as Item | undefined)?.value);

                if (!selectedValue) {
                  return;
                }

                // update to match what handleBrokerageProducerChange is expecting
                const formattedSelectedValue = {
                  user: selectedValue.user,
                  brokerageProducers: [{ id: selectedValue.id }],
                  brokerage: {
                    id: selectedValue.brokerage.id,
                    name: selectedValue.brokerage.name,
                  },
                };
                handleBrokerageProducerChange(formattedSelectedValue);
              }
            }}
            disabled={isSearchBrokerageProducersLoading}
            renderOption={(props, option) =>
              (option as Item)?.value === '' || (option as Item)?.hidden ? null : (
                <BrokerListItem key={option.value} option={option as Item & { email: string }} props={props} />
              )
            }
            renderInput={(params: AutocompleteRenderInputParams) => (
              <TextField
                {...params}
                inputLabel='Broker'
                slotProps={{
                  htmlInput: {
                    ...params.inputProps,
                    value: params.inputProps.value ?? brokerageProducer?.label,
                  },
                }}
              />
            )}
            slotProps={{
              listbox: {
                style: {
                  maxHeight: '300px',
                },
                onScroll: (e) => handleScrollToBottom(e, loadMoreBrokerageProducerResults),
              },
            }}
          />
        ) : (
          <Autocomplete
            disablePortal
            filterOptions={(x) => x}
            sx={!isMobileScreen ? { width: '300px' } : undefined}
            data-testid='Producer'
            fullWidth
            loading={isSearchBrokerageProducersByBrokerageByBrokerageLoading}
            isOptionEqualToValue={(option: Item, value: Item) => option.value === value?.value}
            options={[
              ...(allBrokerageUserProducers
                ? allBrokerageUserProducers
                    .filter((producer) => !!producer.brokerageProducers?.[0]?.id)
                    .map((item) => ({
                      value: item.brokerageProducers?.[0]?.id,
                      label: `${item.user.firstName} ${item.user.lastName}`,
                      email: item.user.email,
                    }))
                : []),
            ]}
            onInputChange={(event, newInputValue) => {
              if (event) {
                debouncedBrokerageUserSearchbarText(newInputValue);
              }
            }}
            value={brokerageProducer ?? null}
            onChange={(_event, value, reason) => {
              if (reason === 'clear') {
                handleAutocompleteClear(1);
                setBrokerageUserSearchbarText('');
                setBrokerageSearchbarText('');
              } else {
                const selectedValue = allBrokerageUserProducers?.find(
                  (b) => b.brokerageProducers?.[0]?.id === (value as Item | undefined)?.value,
                );

                if (selectedValue) {
                  handleBrokerageProducerChange({
                    user: selectedValue.user,
                    brokerageProducers: selectedValue.brokerageProducers,
                    brokerage: {
                      id: selectedValue.brokerageId,
                      name: brokerageAgency?.label || '',
                    },
                  });
                }
              }
            }}
            disabled={!brokerageAgency || isSearchBrokerageProducersByBrokerageByBrokerageLoading}
            renderOption={(props, option) =>
              (option as Item)?.value === '' || (option as Item)?.hidden ? null : (
                <BrokerListItem key={option.value} option={option as Item & { email: string }} props={props} />
              )
            }
            renderInput={(params: AutocompleteRenderInputParams) => (
              <TextField
                {...params}
                inputLabel='Broker'
                slotProps={{
                  htmlInput: {
                    ...params.inputProps,
                    value: params.inputProps.value ?? brokerageProducer?.label,
                  },
                }}
              />
            )}
            slotProps={{
              listbox: {
                style: {
                  maxHeight: '300px',
                },
                onScroll: (e) => handleScrollToBottom(e, loadMoreBrokerageProducerByBrokerageResults),
              },
            }}
            slots={{
              paper: getAddNewProducerOptionComponent,
            }}
          />
        )}
      </Grid>
      {modalType === UserModalTypes.CreateUser && (
        <ThemeProvider theme={composeTheme('light')}>
          <AddEditUserModal
            handleClose={handleCloseModal}
            modalType={modalType}
            selectedBrokerage={brokerageAgency}
            isAddBrokerageUser
            handleNewBrokerageUser={handleNewBrokerageUser}
          />
        </ThemeProvider>
      )}
    </>
  );
};
